import React, { useCallback } from "react";
import styles from "../styles/CompanyProfile.module.css";
import { shouldHideFieldForProfile } from "../utils/common.js";
import AboutCompany from "../components/CompanyProfileSections/FirstTab/AboutCompany";
import ContactsData from "../components/CompanyProfileSections/SecondTab/ContactsData";
import Location from "../components/CompanyProfileSections/SecondTab/Location";
import Branch from "../components/CompanyProfileSections/SecondTab/Branch";
import CommercialOffer from "../components/CompanyProfileSections/ThirdTab/CommercialOffer";
import Skills from "../components/CompanyProfileSections/ThirdTab/Skills";
import Documents from "../components/CompanyProfileSections/ThirdTab/Documents";
import Networking from "../components/CompanyProfileSections/FourthTab/Networking";
import { ReactComponent as EditIcon } from "../assets/icons/CompanyProfile/EditIcon.svg";
import { ReactComponent as CompanyIcon } from "../assets/icons/CompanyProfile/CompanyIcon.svg";
import { ReactComponent as SectorIcon } from "../assets/icons/CompanyProfile/SectorIcon.svg";
import { ReactComponent as PhoneIcon } from "../assets/icons/CompanyProfile/PhoneIcon.svg";
import { ReactComponent as EmailIcon } from "../assets/icons/CompanyProfile/EmailIcon.svg";
import { ReactComponent as WebIcon } from "../assets/icons/CompanyProfile/WebIcon.svg";
import { ReactComponent as XIcon } from "../assets/icons/CompanyProfile/XIcon.svg";
import { ReactComponent as YouTubeIcon } from "../assets/icons/CompanyProfile/YouTubeIcon.svg";
import { ReactComponent as FacebookIcon } from "../assets/icons/CompanyProfile/FacebookIcon.svg";
import { ReactComponent as LinkedInIcon } from "../assets/icons/CompanyProfile/LinkedInIcon.svg";
import { ReactComponent as InstagramIcon } from "../assets/icons/CompanyProfile/InstagramIcon.svg";
import { ReactComponent as OutlinedStarIcon } from "../assets/icons/CompanyProfile/OutlinedStarIcon.svg";

const PROGRESS_LABEL_CENTER_DIVISOR = 2;

const listItemKey = (prefix, value, seenCounts) => {
  const seen = seenCounts.get(value) ?? 0;
  seenCounts.set(value, seen + 1);
  if (seen === 0) {
    return `${prefix}-${value}`;
  }
  return `${prefix}-${value}-${seen}`;
};

const formatSocialMediaUrl = (url) => {
  if (!url) {
    return "";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
};

const isProfileOwner = (user, company) =>
  user?.RFC === company.RFC || user?.esColaborador;

const ProfileChoice = ({ value, label, selected, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect({ value });
  }, [onSelect, value]);
  const className = selected
    ? `${styles.profileButton} ${styles.profileButtonActive}`
    : styles.profileButton;
  return (
    <button className={className} onClick={handleClick}>
      {label}
    </button>
  );
};

export const CompanyProfileHeading = ({
  t,
  company,
  selectedProfile,
  companySizeLabel,
  companyLocationLabel,
  companyStartDate,
}) => {
  const showLegalName = !shouldHideFieldForProfile(
    "razonSocial",
    selectedProfile,
  );
  const showMemberSince =
    !shouldHideFieldForProfile("fechaAltaSac", selectedProfile) &&
    companyStartDate;
  return (
    <div className={styles.profileTitle}>
      <h1 className={styles.companyName}>{company.nombreComercial}</h1>
      {showLegalName ? (
        <p className={styles.companyLegal}>{company.razonSocial}</p>
      ) : (
        <p className={styles.companyLegal}></p>
      )}
      <div className={styles.companyMeta}>
        <span className={styles.companySize}>{companySizeLabel}</span>
        <span className={styles.companyLocation}>{companyLocationLabel}</span>
        {showMemberSince && (
          <>
            <span className={styles.verticalDivider}>|</span>
            <span className={styles.companyMemberSince}>
              {t("pages.companyProfile.memberSince")} {companyStartDate}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export const CompanyProfileOwnerActions = ({
  t,
  user,
  company,
  isConnected,
  connectionLoading,
  eventInvitationLoading,
  onEdit,
  onInvite,
  onConnect,
}) => {
  if (isProfileOwner(user, company)) {
    return (
      <div className={styles.editProfileContainer}>
        <button className={styles.editProfileBtn} onClick={onEdit}>
          <EditIcon className={styles.editProfileIcon} />
          {t("pages.companyProfile.editProfile")}
        </button>
      </div>
    );
  }
  const canInvite = user?.tipoDeUsuario === "11" || user?.compradorActivo;
  const connectClassName = isConnected
    ? `${styles.editProfileBtn} ${styles.disabled}`
    : styles.editProfileBtn;
  return (
    <div style={{ display: "flex", gap: "12px" }}>
      {canInvite && (
        <button
          className={styles.editProfileBtn}
          onClick={onInvite}
          disabled={eventInvitationLoading}
        >
          {t("pages.companyProfile.inviteToEvent")}
        </button>
      )}
      <button
        className={connectClassName}
        onClick={onConnect}
        disabled={isConnected || connectionLoading}
      >
        {t("pages.companyProfile.connect")}
      </button>
    </div>
  );
};

export const CompanyProfileCompletion = ({
  t,
  user,
  company,
  selectedProfile,
  progressPercentage,
  onProfileChange,
}) => {
  if (!isProfileOwner(user, company)) {
    return null;
  }
  const progressAtEdge =
    progressPercentage === "0" || progressPercentage === "100";
  const progressLabelLeft = progressAtEdge
    ? "50%"
    : `${parseInt(progressPercentage) / PROGRESS_LABEL_CENTER_DIVISOR}%`;
  const choices = [
    { value: 0, label: t("pages.companyProfile.profile1") },
    { value: 1, label: t("pages.companyProfile.profile2") },
    { value: 2, label: t("pages.companyProfile.profile3") },
  ];
  return (
    <div className={styles.profileCompletion}>
      <div className={styles.profileButtonsContainer}>
        <span className={styles.availableProfilesLabel}>
          {t("pages.companyProfile.availableProfiles")}
        </span>
        <div className={styles.profileButtons}>
          {choices.map((choice) => (
            <ProfileChoice
              key={choice.value}
              value={choice.value}
              label={choice.label}
              selected={selectedProfile === choice.value}
              onSelect={onProfileChange}
            />
          ))}
        </div>
      </div>
      <p className={styles.completionLabel}>
        {t("pages.companyProfile.completion")}
      </p>
      <div className={styles.progressContainer}>
        <div
          className={styles.progressBar}
          style={{ width: `${progressPercentage}%` }}
        ></div>
        <span
          className={styles.progressLabel}
          style={{ left: progressLabelLeft }}
        >
          {progressPercentage}%
        </span>
      </div>
      {progressPercentage !== "100" && (
        <p className={styles.completionNote}>
          *{t("pages.companyProfile.completionNote")}
        </p>
      )}
    </div>
  );
};

const TabletSection = ({ id, title, collapsed, onToggle, children }) => {
  const handleToggle = useCallback(() => {
    onToggle(id);
  }, [onToggle, id]);
  const contentClassName = collapsed
    ? `${styles.mainSectionContent} ${styles.collapsed}`
    : styles.mainSectionContent;
  return (
    <div className={styles.mainSection}>
      <div className={styles.mainSectionHeader} onClick={handleToggle}>
        <h3 className={styles.mainSectionTitle}>{title}</h3>
      </div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
};

export const CompanyProfileTabletSections = ({
  t,
  company,
  selectedProfile,
  collapsedMainSections,
  onToggleMainSection,
  sectionIds,
}) => (
  <div className={styles.tabletSectionsContainer}>
    <TabletSection
      id={sectionIds.general}
      title={t("pages.companyProfile.section1")}
      collapsed={collapsedMainSections[sectionIds.general]}
      onToggle={onToggleMainSection}
    >
      <AboutCompany company={company} selectedProfile={selectedProfile} />
    </TabletSection>
    <TabletSection
      id={sectionIds.contact}
      title={t("pages.companyProfile.section2")}
      collapsed={collapsedMainSections[sectionIds.contact]}
      onToggle={onToggleMainSection}
    >
      <div className={styles.sectionsContainer}>
        <ContactsData company={company} selectedProfile={selectedProfile} />
        <Location company={company} selectedProfile={selectedProfile} />
        <Branch company={company} selectedProfile={selectedProfile} />
      </div>
    </TabletSection>
    <TabletSection
      id={sectionIds.products}
      title={t("pages.companyProfile.section3")}
      collapsed={collapsedMainSections[sectionIds.products]}
      onToggle={onToggleMainSection}
    >
      <div className={styles.sectionsContainer}>
        <CommercialOffer company={company} selectedProfile={selectedProfile} />
        <Skills company={company} selectedProfile={selectedProfile} />
        <Documents company={company} selectedProfile={selectedProfile} />
      </div>
    </TabletSection>
    <TabletSection
      id={sectionIds.networking}
      title={t("pages.companyProfile.section4")}
      collapsed={collapsedMainSections[sectionIds.networking]}
      onToggle={onToggleMainSection}
    >
      <div className={styles.sectionsContainer}>
        <Networking company={company} selectedProfile={selectedProfile} />
      </div>
    </TabletSection>
  </div>
);

const PhoneLine = ({ countryCode, phone, extension, showExtension }) => (
  <span className={styles.sidebarSectorText}>
    {countryCode} {phone} {showExtension && `Ext. ${extension}`}
  </span>
);

const CompanyProfilePhones = ({
  companyOfficePhone,
  showOfficePhone,
  companyOfficePhoneCountryCode,
  companySecondaryPhone,
  showSecondaryPhone,
  companySecondaryPhoneCountryCode,
  showPhoneExtension,
  companyExtension,
}) => {
  const showOffice = companyOfficePhone && showOfficePhone;
  const showSecondary = companySecondaryPhone && showSecondaryPhone;
  if (!showOffice && !showSecondary) {
    return null;
  }
  return (
    <div className={styles.phoneContainer}>
      <div className={styles.sidebarPhoneInfo}>
        <PhoneIcon className={styles.sidebarIcon} />
        {showOffice && (
          <PhoneLine
            countryCode={companyOfficePhoneCountryCode}
            phone={companyOfficePhone}
            extension={companyExtension}
            showExtension={showPhoneExtension}
          />
        )}
        {!showOffice && showSecondary && (
          <PhoneLine
            countryCode={companySecondaryPhoneCountryCode}
            phone={companySecondaryPhone}
            extension={companyExtension}
            showExtension={showPhoneExtension}
          />
        )}
      </div>
      {showOffice && showSecondary && (
        <div
          className={`${styles.sidebarPhoneInfo} ${styles.secondaryPhoneContainer}`}
        >
          <span
            className={`${styles.sidebarSectorText} ${styles.secondaryPhoneText}`}
          >
            {companySecondaryPhoneCountryCode} {companySecondaryPhone}{" "}
            {showPhoneExtension && `Ext. ${companyExtension}`}
          </span>
        </div>
      )}
    </div>
  );
};

const SocialIconLink = ({ href, children }) => (
  <a
    href={formatSocialMediaUrl(href)}
    target="_blank"
    rel="noopener noreferrer"
    className={styles.socialLink}
  >
    {children}
  </a>
);

const CompanyProfileSocialLinks = ({ t, links }) => {
  const visibleLinks = links.filter((link) => link.href && link.show);
  return (
    <>
      {visibleLinks.length > 0 && (
        <div className={styles.sidebarDigitalPresence}>
          {t("pages.companyProfile.digitalPresence")}:
        </div>
      )}
      <div className={styles.sidebarDigitalPresenceIcons}>
        {visibleLinks.map((link) => (
          <SocialIconLink key={link.id} href={link.href}>
            {link.icon}
          </SocialIconLink>
        ))}
      </div>
    </>
  );
};

const SidebarBlock = ({ title, icon, collapsed, onToggle, sectionId, children }) => {
  const handleToggle = useCallback(() => {
    onToggle(sectionId);
  }, [onToggle, sectionId]);
  const contentClassName = collapsed
    ? `${styles.sidebarContent} ${styles.collapsed}`
    : styles.sidebarContent;
  return (
    <div className={styles.sidebarSection}>
      <div className={styles.sidebarHeader} onClick={handleToggle}>
        {icon}
        <h3 className={styles.sidebarTitle}>{title}</h3>
      </div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
};

export const CompanyProfileSidebar = ({
  t,
  collapsedSections,
  onToggleSection,
  companyGeneralActivity,
  companySector,
  companyOfficePhone,
  showOfficePhone,
  companyOfficePhoneCountryCode,
  companySecondaryPhone,
  showSecondaryPhone,
  companySecondaryPhoneCountryCode,
  showPhoneExtension,
  companyExtension,
  companyEmail,
  showEmail,
  companyWebsite,
  showWebsite,
  companyTwitter,
  showTwitter,
  companyYoutube,
  showYoutube,
  companyFacebook,
  showFacebook,
  companyLinkedin,
  showLinkedIn,
  companyInstagram,
  showInstagram,
  keywords,
  currentLanguage,
  certifications,
}) => {
  const socialLinks = [
    {
      id: "twitter",
      href: companyTwitter,
      show: showTwitter,
      icon: <XIcon sx={{ fontSize: 20 }} />,
    },
    {
      id: "youtube",
      href: companyYoutube,
      show: showYoutube,
      icon: <YouTubeIcon sx={{ fontSize: 20 }} />,
    },
    {
      id: "facebook",
      href: companyFacebook,
      show: showFacebook,
      icon: <FacebookIcon sx={{ fontSize: 20 }} />,
    },
    {
      id: "linkedin",
      href: companyLinkedin,
      show: showLinkedIn,
      icon: <LinkedInIcon sx={{ fontSize: 20 }} />,
    },
    {
      id: "instagram",
      href: companyInstagram,
      show: showInstagram,
      icon: <InstagramIcon sx={{ fontSize: 20 }} />,
    },
  ];
  const keywordKeys = new Map();
  const certificationKeys = new Map();
  return (
    <div className={styles.sidebar}>
      <SidebarBlock
        title={t("pages.companyProfile.generalActivity")}
        icon={<CompanyIcon sx={{ fontSize: 20 }} className={styles.sidebarIcon} />}
        collapsed={collapsedSections.generalActivity}
        onToggle={onToggleSection}
        sectionId="generalActivity"
      >
        <p className={styles.sidebarText}>{companyGeneralActivity}</p>
        <div className={styles.sidebarDivider}></div>
        {companySector && (
          <div className={styles.sidebarSectorInfo}>
            <div className={styles.sidebarSectorInfoLabel}>
              <SectorIcon className={styles.sidebarIcon} />
              <span className={styles.sidebarLabelText}>
                {t("pages.companyProfile.sector")}:
              </span>
            </div>
            <span className={styles.sidebarSectorNameText}>{companySector}</span>
          </div>
        )}
        <CompanyProfilePhones
          companyOfficePhone={companyOfficePhone}
          showOfficePhone={showOfficePhone}
          companyOfficePhoneCountryCode={companyOfficePhoneCountryCode}
          companySecondaryPhone={companySecondaryPhone}
          showSecondaryPhone={showSecondaryPhone}
          companySecondaryPhoneCountryCode={companySecondaryPhoneCountryCode}
          showPhoneExtension={showPhoneExtension}
          companyExtension={companyExtension}
        />
        {companyEmail && showEmail && (
          <div className={styles.sidebarEmailInfo}>
            <EmailIcon className={styles.sidebarIcon} />
            <span className={styles.sidebarSectorText}>{companyEmail}</span>
          </div>
        )}
        {companyWebsite && showWebsite && (
          <div className={styles.sidebarWebsiteInfo}>
            <WebIcon sx={{ fontSize: 18 }} className={styles.sidebarIcon} />
            <span className={styles.sidebarSectorText}>{companyWebsite}</span>
          </div>
        )}
        <CompanyProfileSocialLinks t={t} links={socialLinks} />
      </SidebarBlock>
      <SidebarBlock
        title={t("pages.companyProfile.keywords")}
        icon={
          <OutlinedStarIcon sx={{ fontSize: 20 }} className={styles.sidebarIcon} />
        }
        collapsed={collapsedSections.keywords}
        onToggle={onToggleSection}
        sectionId="keywords"
      >
        <div className={styles.keywordsContainer}>
          <div className={styles.keywordsList}>
            {keywords[currentLanguage]?.map((word) => (
              <span
                key={listItemKey(currentLanguage, word, keywordKeys)}
                className={styles.keywordTag}
              >
                {word}
              </span>
            ))}
          </div>
        </div>
      </SidebarBlock>
      <SidebarBlock
        title={t("pages.companyProfile.certifications")}
        icon={
          <OutlinedStarIcon sx={{ fontSize: 20 }} className={styles.sidebarIcon} />
        }
        collapsed={collapsedSections.certifications}
        onToggle={onToggleSection}
        sectionId="certifications"
      >
        <div className={styles.certificationsContainer}>
          {certifications.map((certification) => (
            <div
              key={listItemKey("certification", certification, certificationKeys)}
              className={styles.certificationItem}
            >
              {certification}
            </div>
          ))}
        </div>
      </SidebarBlock>
    </div>
  );
};
